import React, { useEffect, useState, useRef } from "react";
import { 
  GetDataFromApiWithParams
} from "../../../../../services/api/ExecuteApiRequests";
import { 
  Box, 
  Button, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Container
} from "@mui/material";
import { 
  Search as SearchIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon
} from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import { IPDCToastMessageResult } from "../../../../../components/Controls";
import useDecodedUser from "../../../../../services/hooks/useDecodedUser";
import Barcode from "react-barcode";

export default function PaymentList() {
  // ==================== STATE DECLARATIONS ====================
  const [paymentData, setPaymentData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  
  // Selected record for view dialog
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Toast Notification
  const [toastMessage, setToastMessage] = useState({ message: "", type: "" });
  
  // Ref for printable receipt content
  const receiptContentRef = useRef();
  
  // ==================== HOOKS ====================
  const decodedUser = useDecodedUser();
  
  // ==================== TOAST FUNCTIONS ====================
  const showToast = (message, type = "success") => {
    setToastMessage({ message, type });
  };

  const showSuccessToast = (message) => {
    showToast(message, "success");
  };

  const showErrorToast = (message) => {
    showToast(message, "error");
  };

  const showWarningToast = (message) => {
    showToast(message, "warning");
  };
  const removeLeadingDollar = (str) => {
  if (!str) return str;
  return str.startsWith('$') ? str.substring(1) : str;
};

  // ==================== PRINT FUNCTION ====================
 const handlePrint = useReactToPrint({
  content: () => receiptContentRef.current,
  documentTitle: `Advance_Payment_Receipt_${selectedRow?.id || 'New'}`,
  pageStyle: `
    @page {
      size: auto;
      margin: 45mm 20mm 10mm 20mm; /* top 45mm, right 10mm, bottom 10mm, left 10mm */
    }
    @media print {
      html, body {
        width: 100%;
        margin: 0;
        padding: 0;
        background: white;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      * {
        box-sizing: border-box;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      /* Increase font sizes for all text elements */
      body, .MuiTypography-root {
        font-size: 14pt !important;
      }
      
      /* Header styles */
      .receipt-header h1, .MuiTypography-h5 {
        font-size: 24pt !important;
        font-weight: bold !important;
      }
      
      /* Section titles */
      .MuiTypography-subtitle1 {
        font-size: 16pt !important;
        font-weight: bold !important;
        margin-bottom: 12pt !important;
      }
      
      /* Labels */
      .MuiTypography-body2, .MuiTypography-caption {
        font-size: 12pt !important;
      }
      
      /* Values */
      .MuiTypography-body2, .MuiTypography-body1 {
        font-size: 12pt !important;
      }
      
      /* Amount section */
      .amount-label {
        font-size: 12pt !important;
      }
      .amount-value {
        font-size: 18pt !important;
        font-weight: bold !important;
      }
      .amount-words {
        font-size: 12pt !important;
        font-style: italic !important;
      }
      
      /* Footer text */
      .footer-text {
        font-size: 10pt !important;
      }
      
      /* Bold text */
      strong, .MuiTypography-fontWeightMedium, .MuiTypography-fontWeightBold {
        font-weight: bold !important;
      }
      
      .MuiBox-root, .MuiGrid-root, .MuiTypography-root, .MuiPaper-root {
        color-adjust: exact;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      /* Ensure container uses full width with minimal margins */
      .MuiContainer-root {
        padding-left: 0 !important;
        padding-right: 0 !important;
        max-width: 100% !important;
      }
      
      .MuiGrid-container {
        display: flex !important;
        flex-wrap: wrap !important;
        width: 100% !important;
        margin: 0 !important;
      }
      .MuiGrid-item {
        margin: 0 !important;
        padding: 4px !important;
      }
      .MuiGrid-grid-xs-12 {
        flex: 0 0 100% !important;
        max-width: 100% !important;
        width: 100% !important;
      }
      .MuiGrid-grid-md-6 {
        flex: 0 0 50% !important;
        max-width: 50% !important;
        width: 50% !important;
      }
      
      /* Two column layout */
      .receipt-two-column {
        display: flex !important;
        flex-direction: row !important;
        gap: 12px !important;
        margin-bottom: 20px !important;
        width: 100% !important;
      }
      .receipt-two-column > div {
        flex: 1 !important;
        min-width: 0 !important; /* Prevents overflow */
      }
      
      /* Ensure content stays within margins */
      .receipt-content {
        max-width: 100% !important;
        padding: 0 !important;
      }
      
      /* Official stamp alignment */
      .official-stamp {
        margin-right: 0 !important;
      }
      
      /* Reduce any default padding */
      .MuiDialogContent-root {
        padding: 0 !important;
      }
      
      .MuiBox-root {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
  `,
  onAfterPrint: () => showSuccessToast("Receipt printed successfully"),
  onPrintError: (error) => {
    console.error("Print error:", error);
    showErrorToast("Failed to print receipt");
  }
});

  // ==================== UTILITY FUNCTIONS ====================
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "$0.00";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const numberToWords = (num) => {
    if (!num && num !== 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const numToWords = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
      if (n < 1000000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
      if (n < 1000000000) return numToWords(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + numToWords(n % 1000000) : '');
      return numToWords(Math.floor(n / 1000000000)) + ' Billion' + (n % 1000000000 ? ' ' + numToWords(n % 1000000000) : '');
    };
    
    const dollars = Math.floor(num);
    const cents = Math.round((num - dollars) * 100);
    
    let result = numToWords(dollars) + ' Dollar' + (dollars !== 1 ? 's' : '');
    if (cents > 0) {
      result += ' and ' + numToWords(cents) + ' Cent' + (cents !== 1 ? 's' : '');
    }
    
    return result;
  };

  // ==================== FETCH FUNCTIONS ====================
  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await GetDataFromApiWithParams("finances/piadperiodicpaymentsearch");
      console.log("API Response:", response);
      
      if (response && Array.isArray(response)) {
        const formattedData = response.map((item) => ({
          invoice_no: item.invoice_no,
          agreement_id: item.agreement_id,
          amount: item.amount,
          payment_date: item.payment_date,
          payment_type: item.payment_type,
          bank_name: item.bank_name,
          reference_no: item.reference_no,
          description: item.description,
          if_late_penality_interest_amount: item.if_late_penality_interest_amount,
          recieved_by: item.recieved_by,
          payment_period: item.payment_period,
          arrear_skipped_amount: item.arrear_skipped_amount,
          arrear_skipping_description: item.arrear_skipping_description,
          references_path: item.references_path,
          status: item.status || "pending",
          // Formatted fields for display
          payment_date_formatted: formatDate(item.payment_date),
          amount_formatted: formatCurrency(item.amount),
          late_penalty_formatted: item.if_late_penality_interest_amount ? formatCurrency(item.if_late_penality_interest_amount) : 'N/A',
          arrear_skipped_formatted: item.arrear_skipped_amount ? formatCurrency(item.arrear_skipped_amount) : 'N/A'
        }));
        
        setPaymentData(formattedData);
        setFilteredData(formattedData);
        setTotalRecords(formattedData.length);
      } else {
        setPaymentData([]);
        setFilteredData([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
      showErrorToast("Failed to load payment records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  // ==================== REAL-TIME SEARCH FUNCTION ====================
  const handleSearchChange = (event) => {
    const searchValue = event.target.value.toLowerCase().trim();
    setSearchQuery(event.target.value);
    
    if (!searchValue) {
      setFilteredData(paymentData);
      setTotalRecords(paymentData.length);
      setPage(0);
      return;
    }
    
    const filtered = paymentData.filter(item => 
      (item.invoice_no && item.invoice_no.toString().toLowerCase().includes(searchValue)) ||
      (item.agreement_id && item.agreement_id.toString().toLowerCase().includes(searchValue)) ||
      (item.amount_formatted && item.amount_formatted.toLowerCase().includes(searchValue)) ||
      (item.payment_date_formatted && item.payment_date_formatted.toLowerCase().includes(searchValue)) ||
      (item.payment_type && item.payment_type.toLowerCase().includes(searchValue)) ||
      (item.bank_name && item.bank_name.toLowerCase().includes(searchValue)) ||
      (item.reference_no && item.reference_no.toLowerCase().includes(searchValue)) ||
      (item.description && item.description.toLowerCase().includes(searchValue)) ||
      (item.recieved_by && item.recieved_by.toLowerCase().includes(searchValue)) ||
      (item.payment_period && item.payment_period.toLowerCase().includes(searchValue)) ||
      (item.arrear_skipping_description && item.arrear_skipping_description.toLowerCase().includes(searchValue)) ||
      (item.status && item.status.toLowerCase().includes(searchValue))
    );
    
    setFilteredData(filtered);
    setTotalRecords(filtered.length);
    setPage(0);
  };
  const currencyToNumber = (currencyString) => {
    if (!currencyString) return 0;
    
    // Remove $ symbol and commas, then parse as float
    const numericValue = parseFloat(currencyString.replace(/[$,]/g, ''));
    
    return isNaN(numericValue) ? 0 : numericValue;
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilteredData(paymentData);
    setTotalRecords(paymentData.length);
    setPage(0);
    showWarningToast("Search cleared");
  };

  // ==================== VIEW FUNCTIONS ====================
  const handleViewRecord = (record) => {
    setSelectedRow(record);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRow(null);
  };

  // ==================== PAGINATION FUNCTIONS ====================
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ==================== REFRESH FUNCTIONS ====================
  const handleRefresh = () => {
    fetchPayments();
    setSearchQuery("");
    showWarningToast("Refreshing payment records...");
  };

  // ==================== PAGINATED DATA ====================
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ==================== RENDER ====================
  return (
    <Container maxWidth="xl">
      {/* Toast Notification */}
      {toastMessage.message && (
        <IPDCToastMessageResult 
          message={toastMessage.message} 
          type={toastMessage.type} 
        />
      )}

      {/* Main Title */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Payment Records
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage payment records
        </Typography>
      </Box>
          
      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={10}>
            <TextField
              fullWidth
              label="Search by Invoice No, Agreement ID, Reference No, Description, etc."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              size="small"
              disabled={loading}
              placeholder="Type to filter records..."
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              disabled={loading}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#115293',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {searchQuery && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              variant="outlined"
            >
              Clear Search
            </Button>
          )}
          <Chip 
            label={`Total: ${filteredData.length} records`} 
            variant="outlined"
            size="small"
            color={filteredData.length === 0 ? "error" : "default"}
          />
          {searchQuery && (
            <Chip 
              label={`Filtered: ${filteredData.length} of ${paymentData.length}`} 
              variant="outlined"
              size="small"
              color="info"
            />
          )}
        </Box>
      </Paper>

      {/* Results Table */}
      {console.log("formatted data:", paymentData)}



      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice No</TableCell>
                <TableCell>Agreement ID</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Payment Type</TableCell>
                <TableCell>Bank Name</TableCell>
                <TableCell>Payment Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading payment records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      {searchQuery 
                        ? 'No records found matching your search.' 
                        : 'No payment records found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((record, index) => (
                  <TableRow key={record.invoice_no || index} hover>
                    <TableCell>{record.invoice_no || 'N/A'}</TableCell>
                    <TableCell>{record.agreement_id || 'N/A'}</TableCell>
                    <TableCell>{record.amount}</TableCell>
                    <TableCell>{record.payment_date_formatted}</TableCell>
                    <TableCell>{record.payment_type || 'N/A'}</TableCell>
                    <TableCell>{record.bank_name || 'N/A'}</TableCell>
                    <TableCell>{record.reference_no || 'N/A'}</TableCell>
                    <TableCell>
                      Paid
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleViewRecord(record)}
                        title="View Details"
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '900px',
            borderRadius: 2,
            bgcolor: '#ffffff'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#ffffff',
          borderBottom: '2px solid #1976d2', 
          py: 2 
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src="/images/ipdc logo.jpg"
                alt="IPDC Logo"
                sx={{
                  height: '50px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
              <Box>
                <Typography variant="body1" fontWeight="bold" color="#000">
                  IPDC PAYMENT RECEIPT
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Payment Collection Receipt
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
             
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                size="small"
                disabled={!selectedRow}
                sx={{ 
                  ml: 2,
                  bgcolor: '#9e9e9e',
                  '&:hover': {
                    bgcolor: '#757575'
                  },
                  '&:disabled': {
                    bgcolor: '#e0e0e0'
                  }
                }}
              >
                Print Receipt
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4, bgcolor: '#ffffff' }}>
          <div ref={receiptContentRef}>
            {selectedRow && (
              <>
                <style type="text/css">{`
                  @media print {
                    body { 
                      margin: 0; 
                      padding: 20px; 
                      background: white;
                      -webkit-print-color-adjust: exact;
                      print-color-adjust: exact;
                    }
                  }
                `}</style>

                {/* Receipt Header */}
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  mb: 4, 
                  pb: 2, 
                  borderBottom: '1px dashed #ccc'
                }}>
                  <Box
                    component="img"
                    src="/images/ipdc logo.jpg"
                    alt="IPDC Logo"
                    sx={{
                      height: '150px',
                      width: 'auto',
                      objectFit: 'contain',
                      backgroundColor: '#f0f0f0',
                      display: 'block'
                    }}
                  />
                  
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="body1" fontWeight="bold" color="#000" gutterBottom>
                      Industrial Projects Development Corporation (IPDC)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Payment Collection Receipt
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Invoice No: <Typography variant="caption" sx={{ mt: 1, color: '#000', fontWeight: 'medium' }}>{selectedRow.invoice_no || 'N/A'} </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date: <Typography variant="caption" sx={{ mt: 1, color: '#000', fontWeight: 'medium' }}>{selectedRow.payment_date_formatted}</Typography>
                    </Typography>
                  </Box>
                  
                  {/* Barcode on the right */}
                  <Box sx={{ 
                    width: '150px',
                    height: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 1
                  }}>
                    {selectedRow && (
                      <>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          width: '100%',
                          height: '80px'
                        }}>
                          <Barcode
                            value={selectedRow.invoice_no || selectedRow.agreement_id || 'N/A'}
                            width={1.5}
                            height={60}
                            format="CODE128"
                            displayValue={false}
                            background="#ffffff"
                            lineColor="#000000"
                            margin={0}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ mt: 1, color: '#000', fontWeight: 'medium', textAlign: 'center' }}>
                          {selectedRow.invoice_no || 'N/A'}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Payment Details */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 4,
                  mb: 3
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="#000" gutterBottom sx={{ fontSize: '1.1rem' }}>
                      Payment Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Invoice No:</Typography>
                        <Typography variant="body2" sx={{ color: '#000', fontWeight: 'medium' }}>{selectedRow.invoice_no || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Agreement ID:</Typography>
                        <Typography variant="body2" sx={{ color: '#000', fontWeight: 'medium' }}>{selectedRow.agreement_id || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Payment Type:</Typography>
                        <Typography variant="body2" sx={{ color: '#000' }}>{selectedRow.payment_type || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Payment Period:</Typography>
                        <Typography variant="body2" sx={{ color: '#000' }}>{selectedRow.payment_period || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Bank Name:</Typography>
                        <Typography variant="body2" sx={{ color: '#000' }}>{selectedRow.bank_name || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Reference No:</Typography>
                        <Typography variant="body2" sx={{ color: '#000' }}>{selectedRow.reference_no || 'N/A'}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="#000" gutterBottom sx={{ fontSize: '1.1rem' }}>
                      Additional Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Prepared By:</Typography>
                        <Typography variant="body2" sx={{ color: '#000' }}>{selectedRow.recieved_by || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Late Penalty:</Typography>
                        <Typography variant="body2" sx={{ color: '#000' }}>{selectedRow.late_penalty_formatted !=="$NaN"? selectedRow.late_penalty_formatted:"0"}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Arrear Skipped:</Typography>
                        <Typography variant="body2" sx={{ color: '#000' }}>{selectedRow.arrear_skipped_amount}</Typography>
                      </Box>
                      {selectedRow.arrear_skipping_description && (
                        <Box sx={{ display: 'flex' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ width: '140px' }}>Skip Description:</Typography>
                          <Typography variant="body2" sx={{ color: '#000' }}>{selectedRow.arrear_skipping_description}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Amount Section */}
                <Box sx={{ mb: 3, mt: 2 }}>
                    <Box sx={{ p: 0 }}> {/* Changed from Paper to Box with no padding */}
                        <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1rem' }}>
                            Payment Amount
                            </Typography>
                            <Typography variant="body2"  color="text.secondary" sx={{ fontSize: '1rem' }}>
                            {selectedRow.amount}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1rem' }}>
                            Amount in Words
                            </Typography>
                            <Typography variant="body1" color="text.secondary" fontStyle="italic" sx={{ fontSize: '1rem' }}>
                           [ {selectedRow.amount ? numberToWords(parseFloat(currencyToNumber(selectedRow.amount))) : 'Zero Dollars'} Only]
                            </Typography>
                        </Grid>
                        </Grid>
                    </Box>
                    </Box>

                {/* Description */}
                {selectedRow.description && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary" gutterBottom>
                        Description
                        </Typography>
                        <Box sx={{ p: 0 }}> {/* Changed from Paper to Box with no padding */}
                        <Typography variant="body2" sx={{ color: '#000' }}>{selectedRow.description}</Typography>
                        </Box>
                    </Box>
                    )}

                {/* Footer */}
                <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid #000' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 4,
                      flexWrap: 'wrap',
                      flex: 1
                    }}>
                      <Box>
                        <Typography variant="caption" color="text.primary">Prepared By</Typography>
                        <Typography variant="body2" sx={{ color: '#000', fontWeight: 'medium' }}>
                          {decodedUser?.full_name || decodedUser?.full_name || 'System'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Approved By</Typography>
                        <Typography variant="body2" sx={{ color: '#000', fontWeight: 'medium' }}>
                          { '________________'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <Typography variant="body2" sx={{ color: '#000', fontWeight: 'medium' }}>
                          {new Date().toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>

                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        border: '2px dashed #000', 
                        borderRadius: 2, 
                        width: '220px',
                        textAlign: 'center',
                        bgcolor: '#ffffff'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">
                        OFFICIAL RECEIPT
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        IPDC Finance Department
                      </Typography>
                    </Paper>
                  </Box>
                </Box>

                {/* References Path */}
                {selectedRow.references_path && (
                  <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">
                      Ref: {selectedRow.references_path}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#ffffff', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseViewDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}